import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { FaSignOutAlt } from "react-icons/fa";
import { Link } from "react-router-dom";
import { Header, Loader } from "../../components";
import { AlertasRecurrenciaBanner } from "../../components/AlertasRecurrenciaBanner";
import { CumpleanosBanner } from "../../components/CumpleanosBanner";
import { MENU_CONFIG, UTILITARIAS_BASE, getIpsosItem } from "../../constants/menuConfig";
import { ADMIN_IDS, DEMO_SWITCH_IDS, PRISCILLA_ID, REGION_IDS } from "../../constants/permisos";
import { ROLES } from "../../constants/roles";
import { useAuthContext } from "../../context/auth";
import { client, getClientes, getOportunidades, logout } from "../../utils";
import { getCountCajaPendientes, getCountComprasSinClasificar } from "../../utils/badges";
import { getAllCotizacionesPais, getUsuariosConAgencia } from "../../utils/cotizacionesGuardadas";
import { computePulsoRegional } from "../../utils/pulsoRegional";
import { client as supabase } from "../../utils/supabase";
import styles from "./stylesAdmin.module.scss";

const NOTIF_COT_STORAGE_KEY = "notif_cotizaciones_seen";
const NOTIF_DAYS_LOOKBACK = 30;

const allowedRegionIds = REGION_IDS;

const DAILY_EMOJIS = [
  "😉",
  "😊",
  "😃",
  "🙃",
  "😁",
  "🙂",
  "😎",
  "😄",
  "😌",
  "😏",
  "🤗",
  "👀",
  "🍀",
  "🙌",
  "🫶",
  "👊",
  "👋",
  "🚀",
  "🎯",
  "🔥",
  "🎉",
  "🐢",
  "🏆",
  "🥳",
  "🤩",
  "💪",
  "🦾",
  "🫡",
  "📈",
  "🔝",
  "⚡",
  "💥",
  "🎊",
  "✨",
  "⭐",
  "🌟",
  "🎈",
  "🍾",
  "🧠",
  "☕",
  "🦖",
  "🐙",
  "🦄",
  "🕺",
  "🪩",
  "🌈",
];

// Pool temático exclusivo de Priscilla Munguia (id PRISCILLA_ID) — nunca sale de DAILY_EMOJIS,
// ni en el emoji inicial del día ni en el easter egg del logo (2026-10-01, pedido puntual).
const METAL_EMOJIS = [
  "🤘",
  "🎸",
  "🥁",
  "🎤",
  "🔊",
  "⚡",
  "🔥",
  "💀",
  "☠️",
  "🖤",
  "⛓️",
  "🦇",
  "🐍",
  "🌩️",
  "🎶",
  "👹",
  "🐺",
  "🌑",
  "🏴‍☠️",
  "💥",
];

const getDailyEmoji = (pool) => {
  const getEmojiForDay = (dayOffset) => {
    const date = new Date();
    date.setDate(date.getDate() - dayOffset);
    const seed = date.getFullYear() * 10000 + (date.getMonth() + 1) * 100 + date.getDate();
    return Math.abs(Math.sin(seed) * 10000);
  };

  const recentEmojis = [];
  for (let i = 1; i <= 6; i++) {
    const idx = Math.floor(getEmojiForDay(i) % pool.length);
    recentEmojis.push(pool[idx]);
  }

  const available = pool.filter((e) => !recentEmojis.includes(e));
  const todaySeed = getEmojiForDay(0);
  return available[Math.floor(todaySeed % available.length)];
};

// Ventana anti-repetición del easter egg: no repetir ninguno de los últimos N emojis mostrados,
// calculada sobre el pool que corresponda (siempre menor a su largo, así siempre queda opción).
const getEmojiHistorySize = (pool) => Math.min(20, pool.length - 1);

export const Admin = () => {
  const { pais, session, nombre, rol, setRol, id } = useAuthContext();
  const canSwitchRole = ADMIN_IDS.includes(id);
  const canSwitchDemo = DEMO_SWITCH_IDS.includes(id);
  const demoRoles = [ROLES.COMERCIAL, ROLES.AGENCIA, ROLES.CLIENTE];
  const [propuestasCount, setPropuestasCount] = useState(0);
  const [movPendientesCount, setMovPendientesCount] = useState(0);
  const [comprasSinClasificarCount, setComprasSinClasificarCount] = useState(0);
  // Badges desactivados — SII y Movimientos Banco integrados en Flujo de Caja

  // Easter egg: el emoji del saludo arranca con el del día y cambia al clickear el logo estando
  // en /admin (ver Header.onFloorClick). Estado en memoria — se resetea al recargar. Priscilla
  // Munguia (PRISCILLA_ID) tiene su propio pool temático (METAL_EMOJIS), nunca DAILY_EMOJIS.
  const [welcomeEmoji, setWelcomeEmoji] = useState(() => getDailyEmoji(DAILY_EMOJIS));
  // Historial de los últimos N emojis mostrados (seedeado con el inicial, así el primer click
  // tampoco lo repite). En memoria — se resetea al recargar.
  const emojiHistoryRef = useRef([welcomeEmoji]);
  // Pool activo del easter egg — arranca en DAILY_EMOJIS porque `id` llega null en el primer
  // render (useAuthContext); el useEffect de abajo lo recalcula apenas `id` resuelve a Priscilla.
  const emojiPoolRef = useRef(DAILY_EMOJIS);
  useEffect(() => {
    if (id == null) return;
    const pool = id === PRISCILLA_ID ? METAL_EMOJIS : DAILY_EMOJIS;
    if (pool === emojiPoolRef.current) return;
    emojiPoolRef.current = pool;
    const inicial = getDailyEmoji(pool);
    emojiHistoryRef.current = [inicial];
    setWelcomeEmoji(inicial);
  }, [id]);
  const shuffleWelcomeEmoji = () => {
    const pool = emojiPoolRef.current;
    const historial = emojiHistoryRef.current;
    const opciones = pool.filter((e) => !historial.includes(e));
    const disponibles = opciones.length > 0 ? opciones : pool;
    const siguiente = disponibles[Math.floor(Math.random() * disponibles.length)];
    emojiHistoryRef.current = [...historial, siguiente].slice(-getEmojiHistorySize(pool));
    setWelcomeEmoji(siguiente);
  };

  useEffect(() => {
    if (!pais || !rol || pais === "Region") return;
    const rolesConBadge = [ROLES.SUPERADMIN, ROLES.GERENCIA, ROLES.COMERCIAL, ROLES.BOARD];
    if (!rolesConBadge.includes(rol)) return;

    const otroPais = pais === "Chile" ? "Perú" : "Chile";
    const currentYear = new Date().getFullYear();

    (async () => {
      try {
        const [clientesOtroPais, salesOtroPais, cotizacionesLocales, usuarios] = await Promise.all([
          getClientes({ pais: otroPais }),
          getOportunidades({ pais: otroPais, ano: currentYear }),
          getAllCotizacionesPais(pais),
          getUsuariosConAgencia(pais),
        ]);

        // --- Pulso Regional (solo sales internas del otro país) ---
        const alerts = computePulsoRegional({
          miPais: pais,
          clientesOtroPais,
          salesOtroPais,
        });
        const authUid = session?.user?.id;
        const pulsoSeen = {};
        if (authUid) {
          const { data: seenRows } = await supabase
            .from("pulso_regional_seen")
            .select("empresa_key, seen_at")
            .eq("usuario_id", authUid);
          for (const row of seenRows || []) {
            pulsoSeen[row.empresa_key] = row.seen_at;
          }
        }
        const pulsoUnseen = alerts.filter((a) => {
          const key = `${(a.empresa || "").toLowerCase().trim()}|${a.paisOrigen}`;
          const seenAt = pulsoSeen[key];
          if (!seenAt) return true;
          return new Date(a.fecha) > new Date(seenAt);
        }).length;

        // --- Cotizaciones locales nuevas (últimos 30 días, borradores de agencia/cliente) ---
        const usuariosMap = {};
        for (const u of usuarios) usuariosMap[u.id] = u;

        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() - NOTIF_DAYS_LOOKBACK);

        let cotSeen = {};
        try {
          cotSeen = JSON.parse(localStorage.getItem(NOTIF_COT_STORAGE_KEY) || "{}");
        } catch {
          /* ignore */
        }

        const cotUnseen = cotizacionesLocales.filter((c) => {
          if (!c.created_at || new Date(c.created_at) < cutoff) return false;
          if (c.estado === "ejecutada") return false;
          const rolCreador = usuariosMap[c.usuario_id]?.rol || "";
          if (rolCreador !== "agencia" && rolCreador !== "cliente") return false;
          const seenAt = cotSeen[c.id];
          if (!seenAt) return true;
          return new Date(c.created_at) > new Date(seenAt);
        }).length;

        setPropuestasCount(pulsoUnseen + cotUnseen);
      } catch {
        // silently ignore
      }
    })();
  }, [pais, rol]);

  useEffect(() => {
    if (!pais || pais === "Region") return;
    (async () => {
      try {
        setMovPendientesCount(await getCountCajaPendientes(pais));
      } catch {
        // silently ignore
      }
    })();
  }, [pais]);

  useEffect(() => {
    if (!pais || pais === "Region") return;
    (async () => {
      try {
        setComprasSinClasificarCount(await getCountComprasSinClasificar(pais));
      } catch {
        // silently ignore
      }
    })();
  }, [pais]);

  if (session === undefined) return <Loader fullPage />;

  const nombreUsuario = nombre || "usuario";

  const config = MENU_CONFIG[rol] || {
    utilitarias: () => [],
    ventas: () => [],
    administrativas: () => [],
    financieras: () => [],
  };

  const baseUtilitarias = [...UTILITARIAS_BASE, getIpsosItem(pais)];

  const utilitarias = config.utilitarias ? config.utilitarias({ allowedRegionIds, id, pais }) : baseUtilitarias;

  const ventasRaw = config.ventas({ allowedRegionIds, id, pais });
  const ventas = ventasRaw.map((link) =>
    link.path === "/admin/cotizaciones" && propuestasCount > 0 ? { ...link, badge: propuestasCount } : link
  );
  const administrativasRaw = config.administrativas({ allowedRegionIds, id, pais });
  const administrativas = administrativasRaw.map((link) =>
    link.path === "/admin/facturacion" && comprasSinClasificarCount > 0
      ? { ...link, badge: comprasSinClasificarCount }
      : link
  );
  const financierasRaw = config.financieras({ allowedRegionIds, id, pais });
  const financieras = financierasRaw.map((link) =>
    link.path === "/admin/flujo-caja" && movPendientesCount > 0 ? { ...link, badge: movPendientesCount } : link
  );

  const renderSection = (title, links) =>
    links.length > 0 && (
      <div className={styles.sectionWrapper}>
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>{title}</h2>
          <div className={styles.botones}>
            {links.map((link) => (
              <div key={link.path} className={styles.linkWrapper}>
                {link.badge > 0 && <span className={styles.tileBadge}>{link.badge}</span>}
                {link.disabled ? (
                  <div className={`${styles.link} ${styles.linkDisabled}`}>
                    {link.icon}
                    <span>{link.text}</span>
                  </div>
                ) : link.external ? (
                  <a href={link.path} target="_blank" rel="noopener noreferrer" className={styles.link}>
                    {link.icon}
                    <span>{link.text}</span>
                  </a>
                ) : (
                  <Link to={link.path} className={styles.link}>
                    {link.icon}
                    <span>{link.text}</span>
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );

  return (
    <div className={styles.Admin}>
      <Header
        onFloorClick={shuffleWelcomeEmoji}
        leftContent={
          <p className={styles.welcomeText}>
            <span>Hola {nombreUsuario}</span>
            <span className={styles.welcomeEmoji}>{welcomeEmoji}</span>
          </p>
        }
        centerContent={
          // Reacomodo del header (2026-09-26): Radar Comercial + Cumpleaños se centran como UN
          // GRUPO en el espacio libre entre el emoji y el dropdown de rol — `.headerCenterZone`
          // es un flex simple (`justify-content:center`, `gap:48px`) sobre el 100% del ancho
          // disponible (heredado de `.centerGroup`, `flex-grow:1` en Header.module.scss). Ya NO
          // hace falta el `transform:translateX(-50%)` de antes (retirado — era, de paso, la
          // causa del bug de apilado que se resolvió con el portal a `document.body`, ver ficha
          // de Radar Comercial "Motivo real del bug de apilado"): el panel del Radar se posiciona
          // con `getBoundingClientRect()` sobre su propio botón, así que sigue al botón se mueva
          // donde se mueva. `CumpleanosBanner` deja de tener `margin-left:auto` — ya no está solo
          // en este espacio, es un miembro más del grupo centrado.
          <div className={styles.headerCenterZone}>
            <AlertasRecurrenciaBanner />
            <CumpleanosBanner />
          </div>
        }
      >
        {(canSwitchRole || canSwitchDemo) && (
          <select
            className={styles.rolSelector}
            value={rol}
            onChange={async (e) => {
              const newRol = e.target.value.trim().toLowerCase();
              try {
                const { error } = await client.from("usuarios").update({ rol: newRol }).eq("id", id);
                if (error) throw error;
                setRol(newRol);
                toast.success(`Rol cambiado a ${newRol.toUpperCase()}`);
              } catch (err) {
                toast.error("Error al cambiar rol: " + err.message);
              }
            }}
          >
            {(canSwitchRole ? Object.values(ROLES) : demoRoles).map((r) => (
              <option key={r} value={r}>
                {r.toUpperCase()}
              </option>
            ))}
          </select>
        )}
        <button className={styles.logoutButton} onClick={logout} title="Cerrar sesión">
          <FaSignOutAlt />
        </button>
      </Header>

      <div className={styles.sectionsContainer}>
        {renderSection("UTILITARIAS", utilitarias)}
        {renderSection(rol === ROLES.AGENCIA || rol === ROLES.CLIENTE ? "COMPRAS" : "VENTAS", ventas)}
        {renderSection("ADMINISTRACIÓN", administrativas)}
        {renderSection("FINANCIERAS", financieras)}
      </div>
    </div>
  );
};
